import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';

import Grid from '@mui/material/Grid';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
} from '@stripe/react-stripe-js';

import ElementWrapper from './components/ElementWrapper';

import { Skeleton } from './style';
import { setValueToStore } from '../../../sharedStore';
import { buildBillingDetails } from '..//PaymentCheckoutSelector/components/BankAccountSection/utils';

function CreditCardSelector({
  stripe,
  font,
  onCreatePaymentMethod,
  dataFieldsProps,
}) {
  const [cardCompletedState, setCardCompletedState] = React.useState({
    number: false,
    expire: false,
    cvc: false,
  });
  const elements = useElements();
  const testId = `CreditCardSelector/card-${stripe ? 'loaded' : 'inProgress'}`;

  React.useEffect(() => {
    if (window && stripe) {
      if (!_isEmpty(dataFieldsProps.responses)) {
        setValueToStore('billingDetails', buildBillingDetails(dataFieldsProps));
      }
      setValueToStore('getCardToken', getCardToken);
    }
  }, [stripe]);

  React.useEffect(() => {
    if (Object.values(cardCompletedState).every(Boolean)) {
      createPaymentMethod();
    }
  }, [cardCompletedState]);

  const createPaymentMethod = async () => {
    const cardElement = elements.getElement(CardNumberElement);
    const submitButton = document.querySelector('.ha-form__submit-button');
    submitButton.disabled = true;

    await stripe
      .createPaymentMethod({
        type: 'card',
        card: cardElement,
      })
      .then((result) => {
        if (!result.error) {
          onCreatePaymentMethod(result.paymentMethod, submitButton);
        }
      })
      .finally(() => {
        submitButton.disabled = false;
      });
  };

  const handleCompletedState = (attr) => (value) => {
    setCardCompletedState((prev) => ({ ...prev, [attr]: value }));
  };

  const createToken = async (clientSecret, billingDetails) => {
    const values = {
      client_secret: clientSecret,
    };
    const cardElement = elements.getElement(CardNumberElement);

    await stripe
      .confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: billingDetails,
        },
      })
      .then((result) => {
        if (result.error) {
          values.error = {
            message:
              'There was an error with your card. Please try with another card.',
          };
        } else {
          values.payment_intent = result.payment_intent;
        }
      });

    return values;
  };

  const getCardToken = async (clientSecret, billingDetails) => {
    return await createToken(clientSecret, billingDetails);
  };

  return (
    <form>
      <Grid container spacing={1} data-testid={testId}>
        <Grid item xs={12} sm={7}>
          {stripe && (
            <ElementWrapper
              id="cardnumber"
              label="Card Number*"
              font={font}
              component={CardNumberElement}
              setCompleted={handleCompletedState('number')}
            />
          )}
          {!stripe && <Skeleton />}
        </Grid>
        <Grid item xs={6} sm={3}>
          {stripe && (
            <ElementWrapper
              id="exp-date"
              label="MM/YY*"
              font={font}
              component={CardExpiryElement}
              setCompleted={handleCompletedState('expire')}
            />
          )}
          {!stripe && <Skeleton />}
        </Grid>
        <Grid item xs={6} sm={2}>
          {stripe && (
            <ElementWrapper
              id="cvc"
              label="CVC*"
              font={font}
              component={CardCvcElement}
              setCompleted={handleCompletedState('cvc')}
            />
          )}
          {!stripe && <Skeleton />}
        </Grid>
      </Grid>
    </form>
  );
}

CreditCardSelector.propTypes = {
  font: PropTypes.string,
};

export default CreditCardSelector;
